import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import md5 from 'crypto-js/md5';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { sharedSalesEnabled } from '../../lib/crossCustomerSales';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import DyrejournalSaleListMini from '../../components/dyrejournal/listSaleMini';
import DyrejournalVisitorListCommunicationMini from '../../components/dyrejournal/listVisitorCommunication';
import DyrejournalVisitorJournalsMini from '../../components/dyrejournal/listVisitorJournalsMini';
import DyrejournalReskontoMini from '../../components/dyrejournal/reskontoMini';
import CustomerCreditBadge from '../../components/dyrejournal/customerCreditBadge';
import { isEnabled as customerCreditEnabled } from '../../lib/customerCredit';
import { appConfirm, appAlert } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';
import { conveneErrorMessage } from '../../lib/conveneErrorMessage';
import {
    COLLECTIVE_INVOICE_METHOD_CONVENE,
    canCreateCollectiveInvoiceViaConvene,
    collectiveInvoiceSaleProps,
    isConveneCollectiveInvoice,
} from '../../lib/collectiveInvoiceConvene';

@observer
class VisitorListMini extends Component {
	constructor(props) {
        super(props);
        this.state = {
            dyreIdOwner: {},
            dyreIdAnimals: {},
            creatingCollectiveInvoice: false,
        };
    }

    onClickEditVisitor = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.visitor-card').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState, saleStore, visitorStore } = this.props.stores;
		const { drawerHeightLarge } = appState;

		appState.openDrawer('editVisitor', {
			height: drawerHeightLarge,
            isNew: false,
            id: parseInt(id, 10),
            callback: () => {
                // console.log('onClickEditVisitor callback');
                const { visitor } = visitorStore;
                const { newSale, visitorResults } = saleStore;
                const { visitors } = newSale;
                const idx = visitors.findIndex(a => a.id === visitor.id);
                if (idx > -1) {
                    saleStore.updateObjectKeyArrValue('newSale', 'visitors', idx, visitor);
                }
                const idx2 = visitorResults.findIndex(a => a.id === visitor.id);
                if (idx2 > -1) {
                    saleStore.updateKeyArrValue('visitorResults', idx2, visitor);
                }
            },
		}, drawerLevel + 1);
    }

    onClickRemoveVisitor = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('div').dataset;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        const { visitors } = newSale;
        const newVisitors = visitors.filter(v => v.id !== parseInt(id, 10));
        if (newVisitors.length === 0) {
            // Removing the last owner from a SAVED sale must not leave that
            // sale's id on screen: the next animal pick + save would rewrite the
            // old visit to the new owner/animal in place, orphaning the first
            // animal's journal (TICKET-01622). Same rule as the visitor search.
            saleStore.detachFromPersistedSale();
        }
        saleStore.updateObjectKeyValue('newSale', 'visitors', newVisitors);
        saleStore.updateObjectKeyValue('newSale', 'animals', []);
        saleStore.updateObjectKeyValue('newSale', 'journals', []);
        saleStore.updateKeyValue('visitorAnimals', []);
        saleStore.updateKeyValue('animalJournals', []);
    }

    onClickSendSms = e => {
        e.stopPropagation();
        e.preventDefault();
        const { firstname, lastname, cellphone } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('sendSms', {
			height: drawerHeightMedium,
            isNew: false,
            users: [{
                firstname,
                lastname,
                cellphone,
            }],
            cellphone: cellphone,
            // Allow redirecting the SMS to another number at send time (the registered
            // owner number is not always the right recipient, e.g. animal-welfare orgs
            // whose animals live in foster homes).
            editableRecipient: true,
            callback: () => {
                // console.log('onClickSendSms callback');
            },
		}, drawerLevel + 1);
    }

    onClickSendEmail = e => {
        e.stopPropagation();
        e.preventDefault();
        const { firstname, lastname, email } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('sendEmail', {
            height: drawerHeightMediumLarge,
            users: [{
                firstname,
                lastname,
                email,
            }],
            subject: '',
            body: '',
            editableRecipient: true,
        }, drawerLevel + 1);
    }

    getVisitorJournals = visitorId => {
        const { saleStore } = this.props.stores;
        const { newSale = {} } = saleStore;
        const { journals = [], animals = [] } = newSale;
        const visitorIdInt = parseInt(visitorId, 10);
        const animalIds = new Set(animals
            .filter(animal => parseInt(animal.owner, 10) === visitorIdInt)
            .map(animal => parseInt(animal.id, 10))
            .filter(Number.isFinite));

        return journals.filter(journal => {
            const journalAnimalId = parseInt(journal?.animal?.id || journal?.animal, 10);
            return !!journal?.id && animalIds.has(journalAnimalId);
        });
    }

    onClickSendJournals = e => {
        e.stopPropagation();
        e.preventDefault();
        const { visitorid, email } = e.target.closest('button').dataset;
        const journals = this.getVisitorJournals(visitorid);
        if (journals.length === 0) return;

        const { drawerLevel = 1 } = this.props;
        const { appState, saleStore } = this.props.stores;
        const { drawerHeightLarge } = appState;
        const { newSale = {} } = saleStore;
        const { animals = [] } = newSale;
        const journalAnimalIds = new Set(journals.map(journal => (
            parseInt(journal.animal?.id || journal.animal, 10)
        )));
        const visitorAnimals = animals.filter(animal => journalAnimalIds.has(parseInt(animal.id, 10)));

        appState.openDrawer('sendJournal', {
            hideAdmin: true,
            height: drawerHeightLarge,
            journals,
            animals: visitorAnimals,
            toEmail: email,
        }, drawerLevel + 2);
    }

    onClickCreateNewSaleForVisitor = e => {
        e.stopPropagation();
        e.preventDefault();
        const {
            visitors = [],
        } = this.props;
        const { visitorStore, saleStore, animalStore } = this.props.stores;
        const { currentVisitors } = visitorStore;
        const { newSale } = saleStore;
        const finalVisitors = (visitors && visitors.length > 0) ? (currentVisitors || visitors) : [];
        // Preserve the user field when creating a new sale with visitors.
        // When the saleDateTop feature is on, also carry over the date already
        // picked so choosing a visitor doesn't reset a back-dated visit to now.
        // Only an unsaved sale's date is a user pick — a loaded, persisted
        // sale's saleDate is its historic timestamp and must not leak into the
        // new sale (TICKET-01328); createNewSale falls back to lastSaleDate.
        saleStore.createNewSale({
            visitors: finalVisitors,
            user: newSale?.user,
            saleDate: (saleStore.saleDateTopEnabled && !newSale?.id) ? newSale?.saleDate : undefined,
        });
// We should load the animals and sales for the visitor here.
        const visitorIds = finalVisitors.map(v => v?.id);
        if (visitorIds.length > 0) {
            this.loadAnimals(visitorIds);
            this.loadSales(visitorIds);
        }
        animalStore.updateObjectKeyValue('newAnimal', 'owner', visitorIds[0]);
    }

    onClickCreateNewSaleForThisVisitor = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('button').dataset;
        const { saleStore, animalStore } = this.props.stores;
        const { lastVisitors, newSale: currentSale } = saleStore;
        const visitor = lastVisitors.find(v => v.id === parseInt(id, 10));
        // Preserve the user field when creating a new sale with visitors.
        // When the saleDateTop feature is on, also carry over the date already
        // picked so choosing a visitor doesn't reset a back-dated visit to now.
        // Only an unsaved sale's date is a user pick — a loaded, persisted
        // sale's saleDate is its historic timestamp and must not leak into the
        // new sale (TICKET-01328); createNewSale falls back to lastSaleDate.
        saleStore.createNewSale({
            visitors: [visitor],
            user: currentSale?.user,
            saleDate: (saleStore.saleDateTopEnabled && !currentSale?.id) ? currentSale?.saleDate : undefined,
        });

        const { newSale } = saleStore;
        const { visitors } = newSale;
        const visitorIds = visitors.map(v => v?.id);
        if (visitorIds.length > 0) {
            this.loadAnimals(visitorIds);
            this.loadSales(visitorIds);
        }
        animalStore.updateObjectKeyValue('newAnimal', 'owner', visitor.id);

        // const isProductionAnimals = visitors.some(v => v?.producerNumber);
        // saleStore.updateKeyValue('isProductionAnimals', isProductionAnimals);
    }

    loadAnimals = async visitorIds => {
        const { animalStore, saleStore } = this.props.stores;
        const response = await animalStore.load({
            query: {
                owner: visitorIds,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('visitorAnimals', response);

        await animalStore.load({
            query: {
                owner: visitorIds,
            },
        });
    }

    loadSales = async visitorIds => {
        const { saleStore, userStore } = this.props.stores;
        await saleStore.loadVisitorSales(visitorIds, {
            query: { status: 0 },
            myCustomers: sharedSalesEnabled(userStore),
        });
    }

    loadAll = async (props = this.props) => {
        const { visitors = [] } = props;
        const { saleStore, journalStore, visitorStore } = this.props.stores;
        if (!journalStore.veterinaries || journalStore.veterinaries.length === 0) {
            journalStore.loadVeterinaries();
        }
        const visitorIds = visitors.map(v => v.id);
        saleStore.updateKeyValue('visitorSales', []);
        if (visitorIds && visitorIds.length > 0) {
            const visitorSales = await saleStore.load({ query: { visitor: visitorIds, limit: 200 }, skipUpdate: 1 });
            saleStore.updateKeyValue('visitorSales', visitorSales);

            const currentVisitors = await visitorStore.load({ query: { id: visitorIds }, skipUpdate: 1 });
            visitorStore.updateKeyValue('currentVisitors', currentVisitors);

            const { userStore, visitorCreditStore } = this.props.stores;
            if (customerCreditEnabled(userStore.customer)) {
                visitorIds.forEach(id => visitorCreditStore.loadBalance(id));
            }
        }

        this.loadAllVisitorSalePayments();
    }

    loadAllVisitorSalePayments = async () => {
        const { salePaymentStore, saleStore } = this.props.stores;
        saleStore.updateKeyValue('visitorAllPayments', []);

        const { visitorSales = [] } = saleStore;
        const saleIds = visitorSales.map(e => e.id);
        if (saleIds.length === 0) {
            return false;
        }
        const response = await salePaymentStore.load({
            query: {
                sale: saleIds,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('visitorAllPayments', response);
    }

    dyreIdSearchOwnerPet = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { cellphone } = e.target.closest('button').dataset;
        const { animalStore } = this.props.stores;
        const result = await animalStore.dyreIdSearchOwnerPet(cellphone);
        this.setState({
            dyreIdOwner: {
                [cellphone]: result?.data || { error: 'No data' },
            },
        });
    }

    dyreIdGetOwnerPetsInfoByPhoneNumberEmail = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { cellphone } = e.target.closest('button').dataset;
        const { animalStore } = this.props.stores;
        const result = await animalStore.dyreIdGetOwnerPetsInfoByPhoneNumberEmail(cellphone);
        this.setState({
            dyreIdAnimals: {
                [cellphone]: result?.data || { error: 'No data' },
            },
        });
    }

    onClickNewBooking = async e => {
        e.stopPropagation();
        e.preventDefault();
        const {
            drawerLevel = 1,
            saveSale = () => {},
            saleIsLocked = false,
            visitors = [],
        } = this.props;
		const { appState, saleStore, calendarStore } = this.props.stores;
        const { drawerHeightLarge } = appState;
        const { newSale } = saleStore;

        const { visitor } = e.target.closest('button').dataset;
        const visitorIdInt = parseInt(visitor, 10);

        appState.openDrawer('newCalendarEvent', {
			height: drawerHeightLarge,
            isNew: true,
            hideAdmin: true,
            calendarType: 'booking',
            visitors: visitors.filter(v => v.id === visitorIdInt),
            inputDate: newSale.saleDate,
            saleIsLocked,
            callback: async booking => {
                // console.log('newCalendarEvent callback', booking);
                appState.toggleDrawer(false, drawerLevel + 1);
                calendarStore.resetDragging();
                this.loadAnimalBookings();
            },
		}, drawerLevel + 1);
    }

    onClickToggleCollectiveInvoice = e => {
        e.stopPropagation();
        e.preventDefault();
        const { showCollectiveInvoice } = this.state;
        this.setState({ showCollectiveInvoice: !showCollectiveInvoice });
    }

    onClickCreateCollectiveInvoice = async e => {
        e.stopPropagation();
        e.preventDefault();
        const dialogAction = beginDialogAction(this, 'onClickCreateCollectiveInvoice', () => [
            this.props.stores.saleStore.newSale?.id,
        ]);
        if (!dialogAction) return false;
        try {



            const { creatingCollectiveInvoice } = this.state;
            if (creatingCollectiveInvoice) {
                return false;
            }

            const { visitorid, method } = e.target.closest('button').dataset;
            const visitorIdInt = parseInt(visitorid, 10);
            // TICKET-01715: 'convene' sends the samlefaktura to Convene instead of
            // creating an ordinary invoice. Only offered when the clinic has Convene.
            const { saleStore, userStore } = this.props.stores;
            const { customer } = userStore;
            const viaConvene = isConveneCollectiveInvoice(method) && canCreateCollectiveInvoiceViaConvene(customer);

            const confirmText = viaConvene
                ? 'Er du sikker på at du vil opprette samlefaktura for denne kunden og sende den til Convene?'
                : 'Er du sikker på at du vil opprette samlefaktura for denne kunden?';
            if (!((await appConfirm(confirmText)) && dialogAction.isCurrent())) {
                return false;
            }

            this.setState({ creatingCollectiveInvoice: true });

            const { visitorSales = [], newSale } = saleStore;

            // Back up current newSale since we're inside sale editing
            const newSaleBackup = JSON.parse(JSON.stringify(newSale));

            const salesForVisitor = visitorSales.filter(s => {
                if (s.status !== 1003) return false;
                const { visitors = [] } = s;
                return visitors.some(v => v.id === visitorIdInt);
            });

            if (salesForVisitor.length === 0) {
                this.setState({ creatingCollectiveInvoice: false });
                return false;
            }

            const visitors = salesForVisitor[0].visitors;

            saleStore.createNewSale({
                ...collectiveInvoiceSaleProps({
                    method: viaConvene ? COLLECTIVE_INVOICE_METHOD_CONVENE : undefined,
                    visitors,
                    saleDate: new Date(),
                    dueDate: new Date(Date.now() + (customer.invoiceDueDays || 14) * 24 * 60 * 60 * 1000),
                }),
                skipSearchResults: true,
                skipVeterinaries: true,
                skipVisitorResults: true,
                skipAnimalResults: true,
                skipAnimalJournals: true,
                skipVisitorAnimals: true,
                skipVisitorInvoices: true,
                skipProductResults: true,
                skipVisitorSales: true,
                skipCurrentVisitorSales: true,
                skipCurrentPayments: true,
            });

            const collectiveInvoiceProducts = [];
            for (let j = 0, k = salesForVisitor.length; j < k; j += 1) {
                const sale = salesForVisitor[j];
                sale.products.forEach(product => {
                    collectiveInvoiceProducts.push({
                        saleUser: sale.user,
                        ...product,
                        saleDate: sale.saleDate,
                        saleId: sale.id,
                        saleNumber: sale.saleNumber,
                    });
                });
            }

            // Add invoice fee if enabled on customer
            if (parseInt(customer.collectiveInvoiceFeeEnabled, 10) === 1 && customer.collectiveInvoiceFee > 0) {
                const invoiceFeeProduct = {
                    name: customer.collectiveInvoiceFeeName || 'Fakturagebyr',
                    type: 'invoiceFee',
                    qty: 1,
                    price: customer.collectiveInvoiceFee || 70,
                    account: customer.collectiveInvoiceFeeAccount || 3030,
                    vat: customer.collectiveInvoiceFeeVat || 0,
                    customer: customer.id,
                    createdDate: new Date(),
                };
                collectiveInvoiceProducts.push({
                    ...invoiceFeeProduct,
                    md5: md5(JSON.stringify(invoiceFeeProduct)).toString(),
                });
            }

            saleStore.updateObjectKeyValue('newSale', 'products', collectiveInvoiceProducts);
            saleStore.updateObjectKeyValue('newSale', 'subInvoices', salesForVisitor.map(s => s.id));
            await saleStore.saveSale();

            // TICKET-01715: hand the saved samlefaktura to Convene. On failure the sale
            // still exists (status 0, underbilag attached) and carries conveneLastError,
            // so the vet can open it and use «Betal via Convene» or make it an invoice.
            const createdSaleId = saleStore.newSale?.id;
            let conveneError = '';
            if (viaConvene && createdSaleId > 0) {
                const response = await saleStore.sendSaleToConvene(createdSaleId);
                conveneError = conveneErrorMessage(response);
            }

            // Restore original newSale
            saleStore.updateKeyValue('newSale', newSaleBackup);

            if (conveneError) {
                await appAlert(`Samlefakturaen ble opprettet, men kunne ikke sendes til Convene: ${conveneError} Åpne samlefakturaen og prøv «Betal via Convene» på nytt.`);
            }

            // Reload visitor sales to refresh badge counts
            await this.loadAll();

            this.setState({ creatingCollectiveInvoice: false, showCollectiveInvoice: false });
        } finally {
            dialogAction.finish();
        }
    }

    toggleView = e => {
        const { view } = e.target.closest('button').dataset;
        this.setState({ view });
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.visitors !== this.props.visitors) {
            this.loadAll(nextProps)
        }
    }

    render() {
        const {
            dyreIdOwner = {},
            dyreIdAnimals = {},
            view = this.props.productionCompact ? '' : 'minisales',
            showCollectiveInvoice = false,
            creatingCollectiveInvoice = false,
        } = this.state;
        const {
            visitors = [],
            saleIsLocked = false,
            classNames = '',
            showNewBooking = false,
        } = this.props;
        const { userStore, visitorStore, saleStore, journalStore, visitorCreditStore } = this.props.stores;
        const { balances: creditBalances = {} } = visitorCreditStore || {};
        const { newSale, lastVisitors, calendarNotification, visitorSales = [] } = saleStore;
        const { veterinaries = [] } = journalStore;
        const { user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { currentVisitors } = visitorStore;
        const finalVisitors = (visitors && visitors.length > 0) ? (currentVisitors || visitors) : [];

        return (<>
            {(finalVisitors && finalVisitors.length > 0) && finalVisitors.map(e => {
                const textColor = e.color ? localUtil.isDark(e.color) ? 'text-light' : 'text-dark' : '';
                const visitorCollectiveInvoiceSales = visitorSales.filter(s => {
                    if (s.status !== 1003) return false;
                    const { visitors: saleVisitors = [] } = s;
                    return saleVisitors.some(v => v.id === e.id);
                });
                const visitorJournals = this.getVisitorJournals(e.id);
                return (<>
                    <div class={`d-flex flex-column border box-container mt-1 bg-light p-1 visitor-card ${classNames} font-style-narrow position-relative`}>
                        {!saleIsLocked && <>
                            <div class='position-absolute' style='top: 5px; right: 10px; cursor: pointer;' onClick={this.onClickRemoveVisitor} data-id={e.id}>
                                <i class='fa-solid fa-xmark' />
                            </div>
                        </>}
                        <div class={`d-flex flex-row justify-content-between px-3 py-0 mt-1`}>

                            <div class='d-flex flex-column'>
                                <div class={`d-flex flex-row justify-content-between px-0 py-0`}>
                                    <div class='me-2 visitor-card' onClick={this.onClickEditVisitor} data-id={e.id}>
                                        <i class='fa-solid fa-edit' />
                                    </div>
                                    <div class={`flex-fill ${e.color ? 'rounded-pill px-2' : ''} ${textColor} ${e.color ? 'dj-color-tag' : ''}`} style={`${e.color ? `--dj-tag-color: ${e.color};` : ''}`}><nobr>
                                        <span class='font-weight-lighter me-1'>{e.visitorNumber || e.id}:</span>
                                        {localUtil.showName(e, false, true)}
                                        {e.tripletexCustomerId > 0 && <span class='badge bg-success ms-2' style='font-size: 0.65em;' title={`Tripletex ID: ${e.tripletexCustomerId}`}>Tripletex</span>}
                                        {visitorCollectiveInvoiceSales.length > 0 && <span
                                            class='badge bg-info ms-2'
                                            style='font-size: 0.65em; cursor: pointer;'
                                            onClick={this.onClickToggleCollectiveInvoice}
                                        ><i class='fa-solid fa-file-invoice-dollar me-1' />Samlefaktura ({visitorCollectiveInvoiceSales.length})</span>}
                                        <CustomerCreditBadge balance={creditBalances[e.id] || 0} />
                                    </nobr></div>
                                </div>
                                {e.preferredVeterinary > 0 && (() => {
                                    const preferredVet = veterinaries.find(v => v.id === e.preferredVeterinary);
                                    if (!preferredVet) return null;
                                    return (
                                        <div class='d-flex flex-row align-items-center px-0 py-1 mt-1'>
                                            <span class='badge bg-primary rounded-pill' style='font-size: 0.7em;'>
                                                <i class='fa-solid fa-user-doctor me-1' />
                                                <Text id='dyrejournal.visitor.preferred-veterinary'>Preferred vet</Text>: {preferredVet.firstname} {preferredVet.lastname}
                                            </span>
                                        </div>
                                    );
                                })()}
                                {(() => {
                                    const missingFields = [];
                                    if (!e.firstname) missingFields.push('firstname');
                                    if (!e.lastname) missingFields.push('lastname');
                                    if (!e.email) missingFields.push('email');
                                    if (!e.address) missingFields.push('address');
                                    if (!e.postalcode) missingFields.push('postalcode');
                                    if (!e.place) missingFields.push('place');

                                    if (missingFields.length > 0) {
                                        return (
                                            <div class='d-flex flex-row align-items-center px-0 py-1 mt-1'>
                                                <div class='text-danger' style='font-size: 0.75em;'>
                                                    <i class='fa-solid fa-triangle-exclamation' /> <Text id='dyrejournal.visitor.missing-fields'>Missing fields</Text>: {missingFields.join(', ')}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                                {e.cellphone && <div class={`d-flex flex-row justify-content-between px-0 py-0`}>
                                    <div style='font-size: 0.8em; line-height: 1.3em;'>
                                        <nobr><i class='fa-solid fa-mobile text-muted' /> {e.cellphone} {e.cellphoneComment ? <><span class='text-muted'>({e.cellphoneComment})</span></> : null}</nobr>
                                    </div>
                                </div>}
                                {e.cellphone2 && <div class={`d-flex flex-row justify-content-between px-0 py-0`}>
                                    <div style='font-size: 0.8em; line-height: 1.3em;'>
                                        <nobr><i class='fa-solid fa-mobile text-muted' /> {e.cellphone2} {e.cellphoneComment2 ? <><span class='text-muted'>({e.cellphoneComment2})</span></> : null}</nobr>
                                    </div>
                                </div>}
                                {e.cellphone3 && <div class={`d-flex flex-row justify-content-between px-0 py-0`}>
                                    <div style='font-size: 0.8em; line-height: 1.3em;'>
                                        <nobr><i class='fa-solid fa-mobile text-muted' /> {e.cellphone3} {e.cellphoneComment3 ? <><span class='text-muted'>({e.cellphoneComment3})</span></> : null}</nobr>
                                    </div>
                                </div>}
                                {e.email && <div class={`d-flex flex-row justify-content-between px-0 py-0 text-muted`}>
                                    <div style='font-size: 0.8em; line-height: 1.3em;'>
                                        <nobr><i class='fa-solid fa-envelope' /> {e.email}</nobr>
                                    </div>
                                </div>}
                            </div>

                            <div class='d-flex flex-column pe-4 border-left'>
                                <div class={`d-flex flex-row justify-content-between px-0 py-0`} style='font-size: 0.8em; line-height: 1.3em;'>
                                    <div>{e.address}</div>
                                </div>
                                <div class={`d-flex flex-row justify-content-between px-0 py-0`} style='font-size: 0.8em; line-height: 1.3em;'>
                                    {e.postalcode && <div>{fields.getNumberFixedWidth(e.postalcode)}</div>}
                                    <div class='flex-fill ms-2'>{e.place}</div>
                                </div>
                                {e.birth && <div class={`d-flex flex-row justify-content-between px-0 py-0 text-muted`}>
                                    <div style='font-size: 0.8em; line-height: 1.3em;'>
                                        <nobr><i class='fa-solid fa-cake-candles' /> {util.formatDate(e.birth)}</nobr>
                                    </div>
                                </div>}
                                {e.producerNumber > 0 && <div class={`d-flex flex-row justify-content-between px-0 py-0 text-muted`}>
                                    <div style='font-size: 0.8em; line-height: 1.3em;'>
                                        <nobr><i class='fa-solid fa-sheep' /> {e.producerNumber}</nobr>
                                    </div>
                                </div>}
                            </div>

                            {e.comment ? <>
                                <div class={`d-flex flex-column justify-content-start p-2 mt-3`}>
                                    <div class={`d-flex flex-row px-0 py-0 text-muted`}>
                                        <div>
                                            <i class='fa-duotone fa-circle-info' /> {e.comment}
                                        </div>
                                    </div>
                                </div>
                            </> : <></>}

                        </div>

                        <div class='d-flex flex-row justify-content-end mt-1'>
                            <div class='d-flex flex-row align-items-center'>
                                <button class={`btn btn-sm btn${view === 'minisales' ? '' : '-outline'}-secondary rounded-pill-left py-0`} onClick={this.toggleView} data-view='minisales'>
                                    <nobr><Text id='dyrejournal.visits'>Visits</Text></nobr>
                                </button>
                                <button class={`btn btn-sm btn${view === 'minijournals' ? '' : '-outline'}-secondary rounded-none py-0`} onClick={this.toggleView} data-view='minijournals'>
                                    <nobr><Text id='dyrejournal.journals'>Journals</Text></nobr>
                                </button>
                                <button class={`btn btn-sm btn${view === 'sms' ? '' : '-outline'}-secondary rounded-none py-0`} onClick={this.toggleView} data-view='sms'>
                                    <nobr><Text id='dyrejournal.sms'>SMS</Text></nobr>
                                </button>
                                <button class={`btn btn-sm btn${view === 'email' ? '' : '-outline'}-secondary rounded-none py-0`} onClick={this.toggleView} data-view='email'>
                                    <nobr><Text id='dyrejournal.email'>Email</Text></nobr>
                                </button>
                                <button class={`btn btn-sm btn${view === 'reskonto' ? '' : '-outline'}-secondary rounded-pill-right py-0`} onClick={this.toggleView} data-view='reskonto'>
                                    <nobr><Text id='dyrejournal.reskonto'>Reskonto</Text></nobr>
                                </button>
                            </div>
                        </div>
                        <div class={`d-flex flex-row justify-content-between px-0 py-0 `}>
                            <div class='d-flex flex-column align-items-center w-100 p-0 border-top'>
                                {view === 'minisales' ? <>
                                    <DyrejournalSaleListMini stores={this.props.stores} classNames='w-100' visitors={visitors} showCollectiveInvoice={showCollectiveInvoice} />
                                </> : <></>}
                                {view === 'minijournals' ? <>
                                    <DyrejournalVisitorJournalsMini stores={this.props.stores} classNames='w-100' visitors={visitors} />
                                </> : <></>}
                                {['sms', 'email'].includes(view) ? <>
                                    <DyrejournalVisitorListCommunicationMini
                                        key={`visitor-communication-${e.id}`}
                                        stores={this.props.stores}
                                        classNames='w-100 '
                                        visitorCellphone={e.cellphone}
                                        visitorEmail={e.email}
                                        view={view}
                                    />
                                </> : <></>}
                                {view === 'reskonto' ? <>
                                    <DyrejournalReskontoMini stores={this.props.stores} classNames='w-100' visitors={visitors} />
                                </> : <></>}
                            </div>
                        </div>
                        <div class='d-flex flex-row flex-wrap justify-content-center mt-2'>
                            {e.cellphone && <>
                                <button
                                    class='btn btn-sm btn-outline-success rounded-pill mx-1 py-0 px-2'
                                    onClick={this.onClickSendSms}
                                    data-firstname={e.firstname}
                                    data-lastname={e.lastname}
                                    data-cellphone={e.cellphone}
                                >
                                    <i class='fa-duotone fa-message' /> <Text id='dyrejournal.visitor.create-new-sms-message'>Send new SMS message</Text>
                                </button>
                            </>}
                            {e.email && <>
                                <button
                                    class='btn btn-sm btn-outline-success rounded-pill mx-1 py-0 px-2'
                                    onClick={this.onClickSendEmail}
                                    data-firstname={e.firstname}
                                    data-lastname={e.lastname}
                                    data-email={e.email}
                                >
                                    <i class='fa-duotone fa-envelope' /> <Text id='dyrejournal.visitor.create-new-email-message'>Send new email</Text>
                                </button>
                            </>}
                            {e.email && visitorJournals.length > 0 && <>
                                <button
                                    class='btn btn-sm btn-outline-success rounded-pill mx-1 py-0 px-2'
                                    onClick={this.onClickSendJournals}
                                    data-visitorid={e.id}
                                    data-email={e.email}
                                >
                                    <i class='fa-duotone fa-file-medical' /> <Text
                                        id='dyrejournal.visitor.send-journal-email'
                                    >Send journal</Text>
                                </button>
                            </>}
                            {newSale.id && <>
                                <button class='btn btn-sm btn-outline-primary rounded-pill mx-1 py-0 px-2' onClick={this.onClickCreateNewSaleForVisitor}>
                                    <i class='fa-duotone fa-plus' /> <Text id='dyrejournal.visitor.create-new-sale'>Create new sale for visitor</Text>
                                </button>
                            </>}
                            {showNewBooking && !this.props.productionCompact && <button class='btn btn-sm btn-outline-success rounded-pill mx-1 py-0' onClick={this.onClickNewBooking} data-visitor={e.id}>
                                <i class='fa-duotone fa-calendar' /> <Text id='dyrejournal.booking.new'>New booking</Text>
                            </button>}
                            {showCollectiveInvoice && visitorCollectiveInvoiceSales.length > 0 && <>
                                <button
                                    class='btn btn-sm btn-outline-warning rounded-pill mx-1 py-0 px-2'
                                    onClick={this.onClickCreateCollectiveInvoice}
                                    data-visitorid={e.id}
                                    disabled={creatingCollectiveInvoice}
                                >
                                    {creatingCollectiveInvoice
                                        ? <><i class='fa-solid fa-spinner fa-spin me-1' />Oppretter samlefaktura...</>
                                        : <><i class='fa-solid fa-file-invoice-dollar me-1' />Opprett samlefaktura</>
                                    }
                                </button>
                                {canCreateCollectiveInvoiceViaConvene(userStore.customer) && <button
                                    class='btn btn-sm btn-outline-primary rounded-pill mx-1 py-0 px-2'
                                    onClick={this.onClickCreateCollectiveInvoice}
                                    data-visitorid={e.id}
                                    data-method={COLLECTIVE_INVOICE_METHOD_CONVENE}
                                    disabled={creatingCollectiveInvoice}
                                    title='Opprett samlefaktura og send den til Convene'
                                >
                                    <i class='fa-duotone fa-file-invoice me-1' />Samlefaktura via Convene
                                </button>}
                            </>}
                        </div>


                        {/*
                            TODO: List out all medical records for this visitor.
                            TODO: List out all outstanding payments for this visitor.
                            TODO: List last 5 visits for this visitor.
                            TODO: List recommendations for this visitor.
                            TODO: Add fields for sms and email campaigns.
                            TODO: Add visit method (in person, phone, email, sms, etc.)
                        */}

                    </div>
                </>);
            })}
            {!(finalVisitors && finalVisitors.length > 0) && !this.props.productionCompact && <>
                <div class={`d-flex flex-column justify-content-center w-100 my-1 text-muted ${classNames}`}>
                    {lastVisitors && lastVisitors.length > 0 && <>
                        <small><Text id='dyrejournal.sale-last-visitors'>Last visitors:</Text></small>
                        <div class='d-flex flex-wrap'>
                            {lastVisitors.map(e => <>
                                <div class={`d-flex flex-row justify-content-between px-0 py-0`} style='font-size: 0.9em; line-height: 1.2em;'>
                                    <button
                                        class='btn btn-sm btn-outline-primary rounded-pill mx-1 mt-1'
                                        style='font-size: 0.8em; line-height: 1.2em;'
                                        onClick={this.onClickCreateNewSaleForThisVisitor}
                                        data-id={e.id}
                                    >
                                        <i class='fa-duotone fa-user' /> {localUtil.showName(e, true)} - {e.cellphone}
                                    </button>
                                </div>
                            </>)}
                        </div>
                    </>}

                    <div class='w-100 text-center' style='font-size: 1.5em;'>
                        <i class='fa-solid fa-user' />
                    </div>
                    <div class='w-100 text-center' style='font-size: 1.0em;'>
                        <Text id='dyrejournal.visitor.not-selected'>No visitor selected</Text>
                    </div>
                </div>
            </>}
        </>);
    }
}

export default VisitorListMini;
